import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Check, Circle, CircleDot, ExternalLink, History, Loader2, MessageSquarePlus, Undo2, Wand2, X } from "lucide-react";
import { ApiError, useApi } from "@ncp/api-client";
import { AskUserCard, AssistantApi, type AskUserEvent, type AssistantStreamEvent, type ThreadMessage, type ToolConfirmation } from "@ncp/assistant-sdk";
import { useLocale } from "@ncp/i18n";
import { Badge, Button, ChatPanel, cn, PromptInputAttachment, ToggleGroup, ToggleGroupItem, WorkSteps, type ChatMessageLike, type WorkStepLike } from "@ncp/ui";

interface PlanStep { title: string; status: string }
interface Plan { summary?: string | null; steps: PlanStep[] }
interface Card { id: string; title: string; description: string; status: string; outcome?: string | null; link?: string | null; error?: string | null }
interface Turn {
  id: string;
  role: "user" | "assistant";
  content: string;
  streaming?: boolean;
  stage?: string | null;
  plan?: Plan | null;
  steps: WorkStepLike[];
  cards: Card[];
  attachments?: Array<{ name: string; status?: "uploading" | "uploaded" | "failed" }>;
  error?: string | null;
  /** The author's questions before it acts (`ask_user`, builder §20); the turn ended with them. */
  ask?: AskUserEvent | null;
  /** The server's id of the assistant message (the final frame's), for "Undo this turn". */
  messageId?: string | null;
  /** `extras.checkpoint` of a turn that changed files: shown as "Undo this turn" while files > 0 and it is neither undone nor pruned. */
  checkpoint?: { files: number; undoneAt?: string | null; pruned?: boolean | null } | null;
  undo?: { state: "busy" | "done" | "error"; message?: string | null } | null;
}

/** A checkpoint the author's turn left (`extras.checkpoint`, builder §20), read defensively. */
function checkpointOf(extras: Record<string, unknown> | null | undefined): Turn["checkpoint"] {
  const c = extras?.checkpoint as Record<string, unknown> | undefined;
  if (!c || typeof c !== "object") return null;
  const files = typeof c.files === "number" ? c.files : 0;
  return { files, undoneAt: typeof c.undoneAt === "string" ? c.undoneAt : null, pruned: c.pruned === true };
}
const undoable = (t: Turn) => !!t.checkpoint && t.checkpoint.files > 0 && !t.checkpoint.undoneAt && !t.checkpoint.pruned;

interface AuthorThread { id: string; title: string; lastMessageAt: string; messages: number; preview?: string | null }

export interface AuthorPanelProps {
  /** The draft the author works on. */
  definitionId: string;
  pageName?: string | null;
  /** Sent once when the panel opens on a draft without a conversation (a page made from chat is built from its description). */
  initialMessage?: string | null;
  /** The file open in the editor; "this" in a request means it. */
  selection?: string | null;
  /** What the Studio shows (the open file's excerpt, the latest findings), sent as data with each message. */
  context?: Record<string, unknown> | null;
  /** After a turn or a confirmed card: the draft's files may have changed. */
  onChanged?: () => void;
  /** A card's result links somewhere (a page, a dataset). */
  onOpenLink?: (link: string) => void;
  suggestions?: string[];
  className?: string;
}

const parse = (s?: string | null): Record<string, unknown> | null => {
  if (!s) return null;
  try { const v = JSON.parse(s); return v && typeof v === "object" ? v as Record<string, unknown> : null; } catch { return null; }
};

/** What a tool call did, in words, from its key and arguments. */
function stepLabel(tool: string, args: Record<string, unknown> | null, ar: boolean): string {
  const path = typeof args?.path === "string" ? args.path : null;
  const L = (en: string, a: string) => (ar ? a : en);
  switch (tool) {
    case "author.plan": return L("Planned the work", "خطط للعمل");
    case "author.files.list": return L("Listed the files", "عرض الملفات");
    case "author.files.read": return L(`Read ${path ?? "a file"}`, `قرأ ${path ?? "ملفا"}`);
    case "author.files.write": return L(`Wrote ${path ?? "a file"}`, `كتب ${path ?? "ملفا"}`);
    case "author.files.edit": return L(`Edited ${path ?? "a file"}`, `عدل ${path ?? "ملفا"}`);
    case "author.files.delete": return L("Deleted files", "حذف ملفات");
    case "author.build": return L("Built the page", "بنى الصفحة");
    case "author.check": return L("Checked the page in English and Arabic", "فحص الصفحة بالعربية والإنجليزية");
    case "author.catalogue": return L(`Looked up ${String(args?.section ?? "the catalogue")}${args?.query ? `: ${String(args.query)}` : ""}`, `بحث في ${String(args?.section ?? "الفهرس")}${args?.query ? `: ${String(args.query)}` : ""}`);
    case "author.data.import": return L(`Imported ${String(args?.attachment ?? "a file")}`, `استورد ${String(args?.attachment ?? "ملفا")}`);
    case "author.packages.request": return L(`Requested the package ${String(args?.name ?? "")}`, `طلب الحزمة ${String(args?.name ?? "")}`);
    case "author.capabilities.request": return L("Requested a new capability", "طلب قدرة جديدة");
    case "data.search": return L(`Searched the data${args?.query ? ` for "${String(args.query)}"` : ""}`, `بحث في البيانات${args?.query ? ` عن «${String(args.query)}»` : ""}`);
    case "data.catalog": return L("Read the data catalogue", "قرأ فهرس البيانات");
    case "data.query": return L(`Queried ${String(args?.dataset ?? "a dataset")}`, `استعلم عن ${String(args?.dataset ?? "مجموعة بيانات")}`);
    case "data.get": return L("Read a record", "قرأ سجلا");
    default: return tool;
  }
}

/** The quick check's verdict in a write tool's answer (the answer may be cut short, so it is matched rather than parsed). */
const quickVerdict = (summary?: string | null) => /"quickCheck":\{"status":"(pass|warn|fail)"/.exec(summary ?? "")?.[1] ?? null;

function PlanList({ plan }: { plan: Plan }) {
  return (
    <ol className="mb-3 space-y-1 rounded-xl border border-sand-200 bg-sand-50/60 p-3 text-sm">
      {plan.summary ? <li className="mb-1 text-xs font-medium text-sand-700">{plan.summary}</li> : null}
      {plan.steps.map((s, i) => (
        <li key={i} className="flex items-start gap-2">
          {s.status === "done" ? <Check className="mt-0.5 size-3.5 shrink-0 text-success-600" aria-hidden />
            : s.status === "doing" ? <CircleDot className="mt-0.5 size-3.5 shrink-0 text-aegold-700" aria-hidden />
            : <Circle className="mt-0.5 size-3.5 shrink-0 text-sand-400" aria-hidden />}
          <span className={cn(s.status === "done" && "text-muted-foreground", s.status === "skipped" && "line-through text-muted-foreground")}>{s.title}</span>
        </li>
      ))}
    </ol>
  );
}

function ConfirmCard({ card, onDecide, onOpenLink }: { card: Card; onDecide: (accept: boolean) => void; onOpenLink?: (link: string) => void }) {
  const { locale } = useLocale();
  const ar = locale === "ar";
  const pending = card.status === "Pending";
  const busy = card.status === "deciding";
  return (
    <div className="mt-3 rounded-xl border border-aegold-200 bg-aegold-50/40 p-3">
      <p className="text-sm font-medium">{card.title}</p>
      {card.description ? <p className="mt-0.5 text-xs text-muted-foreground">{card.description}</p> : null}
      {pending || busy ? (
        <div className="mt-2 flex gap-2">
          <Button size="sm" disabled={busy} onClick={() => onDecide(true)}>{busy ? <Loader2 className="size-3.5 animate-spin" /> : <Check className="size-3.5" />}{ar ? "تأكيد" : "Confirm"}</Button>
          <Button size="sm" variant="ghost" disabled={busy} onClick={() => onDecide(false)}><X className="size-3.5" />{ar ? "إلغاء" : "Cancel"}</Button>
        </div>
      ) : (
        <div className="mt-2 space-y-1">
          <Badge variant={card.status === "Executed" ? "success" : card.status === "Failed" ? "danger" : "outline"} size="sm">
            {card.status === "Executed" ? (ar ? "نفذ" : "Done") : card.status === "Declined" ? (ar ? "ألغي" : "Cancelled") : card.status === "Failed" ? (ar ? "تعذر" : "Failed") : card.status}
          </Badge>
          {card.outcome || card.error ? <p className="text-xs text-muted-foreground">{card.error ?? card.outcome}</p> : null}
          {card.link && onOpenLink ? (
            <Button size="sm" variant="outline" onClick={() => onOpenLink(card.link!)}><ExternalLink className="size-3.5" />{ar ? "فتح" : "Open"}</Button>
          ) : null}
        </div>
      )}
    </div>
  );
}

function UndoTurn({ turn, ar, onUndo }: { turn: Turn; ar: boolean; onUndo: () => void }) {
  const state = turn.undo?.state ?? null;
  if (state === "done") return <p className="mt-2 text-xs text-muted-foreground">{turn.undo?.message || (ar ? "أعيدت الملفات كما كانت قبل هذه الخطوة." : "The files are back as they were before this turn.")}</p>;
  return (
    <div className="mt-2 flex flex-wrap items-center gap-2">
      {undoable(turn) ? (
        <Button size="sm" variant="ghost" disabled={state === "busy"} onClick={onUndo}>
          {state === "busy" ? <Loader2 className="size-3.5 animate-spin" /> : <Undo2 className="size-3.5" />}
          {ar ? "التراجع عن هذه الخطوة" : "Undo this turn"}
          {turn.checkpoint ? <span className="text-xs text-muted-foreground">· {ar ? `الملفات: ${turn.checkpoint.files}` : `${turn.checkpoint.files} file${turn.checkpoint.files === 1 ? "" : "s"}`}</span> : null}
        </Button>
      ) : null}
      {state === "error" && turn.undo?.message ? <span className="text-xs text-danger-700">{turn.undo.message}</span> : null}
    </div>
  );
}

function fromHistory(messages: ThreadMessage[], confirmations: ToolConfirmation[], ar: boolean): Turn[] {
  return messages.filter((m) => m.role === "User" || m.role === "Assistant").map((m) => ({
    id: m.id,
    role: m.role === "User" ? "user" as const : "assistant" as const,
    content: m.content,
    steps: (m.toolCalls ?? []).map((c, i) => ({
      id: `${m.id}-${i}`, label: stepLabel(c.tool, parse(c.argsSummary), ar), status: c.status === "error" || c.status === "failed" ? "error" as const : "done" as const, durationMs: c.durationMs,
    })),
    cards: confirmations.filter((c) => c.messageId === m.id).map((c) => ({
      id: c.id, title: (ar && c.titleAr) || c.title, description: (ar && c.descriptionAr) || c.description, status: c.status,
      outcome: (ar && c.result?.summaryAr) || c.resultSummary, link: c.result?.link ?? null, error: c.error,
    })),
    attachments: m.role === "User" ? (m.attachments ?? []).map((a) => ({ name: a.fileName, status: "uploaded" as const })) : undefined,
    messageId: m.role === "Assistant" ? m.id : null,
    checkpoint: m.role === "Assistant" ? checkpointOf(m.extras as Record<string, unknown> | null | undefined) : null,
    ask: m.role === "Assistant" ? (m.extras?.ask as AskUserEvent | null | undefined) ?? null : null,
  }));
}

/**
 * The AI author on a page draft: a conversation that plans, reads and changes the draft's files, builds and checks them (in English and
 * Arabic, with an accessibility check), looks up data and capabilities, imports attached files and files package or capability requests.
 * Plan mode only reads and proposes. Used by Page Studio, "My pages" and the private copy behind "Suggest a change".
 */
export function AuthorPanel({ definitionId, pageName, initialMessage, selection, context, onChanged, onOpenLink, suggestions, className }: AuthorPanelProps) {
  const api = useApi();
  const assistant = useMemo(() => new AssistantApi(api), [api]);
  const { locale } = useLocale();
  const ar = locale === "ar";
  const base = `/api/platform/pages/versions/${encodeURIComponent(definitionId)}/author`;
  const [threads, setThreads] = useState<AuthorThread[]>([]);
  const [threadId, setThreadId] = useState<string | null>(null);
  const [turns, setTurns] = useState<Turn[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState<"build" | "plan">("build");
  const [pending, setPending] = useState<File[]>([]);
  const [showThreads, setShowThreads] = useState(false);
  const abort = useRef<AbortController | null>(null);
  const threadIdRef = useRef<string | null>(null);
  threadIdRef.current = threadId;
  const started = useRef(false);
  const latest = useRef({ selection, context, onChanged, mode, locale });
  latest.current = { selection, context, onChanged, mode, locale };

  const load = useCallback(async (id: string | null) => {
    setThreadId(id);
    if (!id) { setTurns([]); return; }
    const detail = await assistant.thread(id);
    setTurns(fromHistory(detail.messages, detail.confirmations ?? [], ar));
  }, [assistant, ar]);

  useEffect(() => {
    let live = true;
    setLoaded(false);
    started.current = false;
    api.get<AuthorThread[]>(`${base}/threads`).then(async (list) => {
      if (!live) return;
      setThreads(list);
      await load(list[0]?.id ?? null);
      if (live) setLoaded(true);
    }, () => live && setLoaded(true));
    return () => { live = false; abort.current?.abort(); };
  }, [api, base, load]);

  const patch = (id: string, f: (t: Turn) => Turn) => setTurns((all) => all.map((t) => (t.id === id ? f(t) : t)));

  const send = useCallback(async (text: string) => {
    if (busy) return;
    const files = pending;
    setPending([]);
    const userId = `u-${Date.now()}`;
    const answerId = `a-${Date.now()}`;
    setTurns((all) => [...all,
      { id: userId, role: "user", content: text, steps: [], cards: [], attachments: files.map((f) => ({ name: f.name, status: "uploading" as const })) },
      { id: answerId, role: "assistant", content: "", streaming: true, steps: [], cards: [], stage: "thinking" }]);
    setBusy(true);
    const ac = new AbortController();
    abort.current = ac;
    let n = 0;
    try {
      let tid = threadId;
      if (!tid) {
        tid = (await api.post<{ id: string }>(`${base}/threads?locale=${latest.current.locale}`, {})).id;
        setThreadId(tid);
      }
      let attachmentIds: string[] = [];
      if (files.length) {
        const up = await assistant.attach(tid, files);
        attachmentIds = up.attachments.map((a) => a.documentId);
        const ok = new Set(up.attachments.map((a) => a.fileName));
        patch(userId, (t) => ({ ...t, attachments: files.map((f) => ({ name: f.name, status: ok.has(f.name) ? "uploaded" as const : "failed" as const })) }));
      }
      const { selection: sel, context: ctx, mode: m } = latest.current;
      const body = { message: text, threadId: tid, attachmentIds, locale: latest.current.locale, mode: m, selection: sel ?? null, context: ctx ?? null };
      for await (const e of api.streamEvents<AssistantStreamEvent>(base, body, ac.signal)) {
        switch (e.type) {
          case "status":
            if (e.stage === "thread" && e.detail) setThreadId(e.detail);
            else if (e.stage === "plan") { const p = parse(e.detail); if (p) patch(answerId, (t) => ({ ...t, plan: { summary: (p.summary as string) ?? null, steps: (p.steps as PlanStep[]) ?? [] } })); }
            else patch(answerId, (t) => ({ ...t, stage: e.stage }));
            break;
          case "tool": {
            const args = e.phase === "start" ? parse(e.summary) : null;
            if (e.phase === "start") {
              const id = `${answerId}-${n++}`;
              patch(answerId, (t) => ({ ...t, steps: [...t.steps, { id, label: stepLabel(e.tool, args, ar), status: "running", detail: e.tool }] }));
            } else {
              const verdict = quickVerdict(e.summary);
              patch(answerId, (t) => {
                const steps = [...t.steps];
                const i = steps.map((s) => s.detail).lastIndexOf(e.tool);
                if (i >= 0) steps[i] = { ...steps[i], status: e.phase === "error" ? "error" : "done", durationMs: e.durationMs ?? null,
                  label: verdict ? `${steps[i].label} · ${verdict === "pass" ? (ar ? "سليم" : "checks pass") : verdict === "warn" ? (ar ? "تحذيرات" : "warnings") : (ar ? "أخطاء" : "errors")}` : steps[i].label };
                return { ...t, steps };
              });
            }
            break;
          }
          case "token":
            patch(answerId, (t) => ({ ...t, content: t.content + e.text }));
            break;
          case "confirm":
            patch(answerId, (t) => ({ ...t, cards: [...t.cards, { id: e.confirmationId, title: e.title, description: e.description, status: "Pending" }] }));
            break;
          case "final":
            patch(answerId, (t) => ({ ...t, content: e.content, streaming: false, messageId: e.messageId }));
            break;
          case "ask_user":
            patch(answerId, (t) => ({ ...t, ask: { intro: e.intro ?? null, questions: e.questions ?? [] } }));
            break;
          case "error":
            patch(answerId, (t) => ({ ...t, error: e.message, streaming: false }));
            break;
        }
      }
    } catch (e) {
      if (!(e instanceof DOMException && e.name === "AbortError")) {
        const message = e instanceof ApiError ? e.problem?.error ?? e.message : String(e);
        patch(answerId, (t) => ({ ...t, error: message }));
      }
    } finally {
      patch(answerId, (t) => ({ ...t, streaming: false, stage: null, steps: t.steps.map((s) => (s.status === "running" ? { ...s, status: "done" as const } : s)) }));
      setBusy(false);
      abort.current = null;
      latest.current.onChanged?.();
      api.get<AuthorThread[]>(`${base}/threads`).then(setThreads, () => undefined);
      // The turn's checkpoint (files it changed, for "Undo this turn") is stored with the message: read it back once the turn ended.
      const tid = threadIdRef.current;
      if (tid) void assistant.thread(tid).then((detail) => {
        const server = fromHistory(detail.messages, detail.confirmations ?? [], ar);
        const last = server.filter((x) => x.role === "assistant").at(-1);
        if (last) patch(answerId, (t) => ({ ...t, messageId: t.messageId ?? last.messageId, checkpoint: last.checkpoint, ask: t.ask ?? last.ask }));
      }, () => undefined);
    }
  }, [api, assistant, base, busy, pending, threadId, ar]);

  // "Undo this turn" (builder §20): restores the files the turn changed, as they were before it; refused with a plain reason when the turn was
  // already undone, is older than the last 20 turns, was too large, or a file changed after it.
  const undoTurn = async (turn: Turn) => {
    if (!turn.messageId) return;
    patch(turn.id, (t) => ({ ...t, undo: { state: "busy" } }));
    try {
      const r = await api.post<{ summary?: string | null; restored?: string[]; removed?: string[] }>(`/api/assistant/author/turns/${encodeURIComponent(turn.messageId)}/undo`, {});
      patch(turn.id, (t) => ({ ...t, undo: { state: "done", message: r.summary ?? null }, checkpoint: t.checkpoint ? { ...t.checkpoint, undoneAt: new Date().toISOString() } : t.checkpoint }));
      latest.current.onChanged?.();
    } catch (e) {
      patch(turn.id, (t) => ({ ...t, undo: { state: "error", message: e instanceof ApiError ? e.problem?.error ?? e.problem?.detail ?? e.message : String(e) } }));
    }
  };

  // A page made from chat opens with its description sent as the first request.
  useEffect(() => {
    if (!loaded || started.current || !initialMessage || turns.length > 0) return;
    started.current = true;
    void send(initialMessage);
  }, [loaded, initialMessage, turns.length, send]);

  const decide = async (turnId: string, card: Card, accept: boolean) => {
    if (!threadId) return;
    patch(turnId, (t) => ({ ...t, cards: t.cards.map((c) => (c.id === card.id ? { ...c, status: "deciding" } : c)) }));
    try {
      const r = await assistant.decideConfirmation(threadId, card.id, accept);
      const c = r.confirmation;
      patch(turnId, (t) => ({ ...t, cards: t.cards.map((x) => (x.id === card.id ? { ...x, status: c.status, outcome: (ar && c.result?.summaryAr) || c.resultSummary || r.message?.content, link: c.result?.link ?? null, error: c.error } : x)) }));
      latest.current.onChanged?.();
    } catch (e) {
      patch(turnId, (t) => ({ ...t, cards: t.cards.map((x) => (x.id === card.id ? { ...x, status: "Failed", error: e instanceof ApiError ? e.problem?.error ?? e.message : String(e) } : x)) }));
    }
  };

  const messages: ChatMessageLike[] = turns.map((t) => ({
    id: t.id,
    role: t.role,
    content: t.error ? `${t.content}${t.content ? "\n\n" : ""}> ${t.error}` : t.content,
    streaming: t.streaming,
    attachments: t.attachments,
    before: t.role === "assistant" ? (
      <>
        {t.plan && t.plan.steps.length > 0 ? <PlanList plan={t.plan} /> : null}
        <WorkSteps steps={t.steps} running={!!t.streaming && t.steps.some((s) => s.status === "running")} />
      </>
    ) : undefined,
    after: t.cards.length > 0 || t.ask || (t.role === "assistant" && (undoable(t) || t.undo)) ? (
      <>
        {t.cards.map((c) => <ConfirmCard key={c.id} card={c} onDecide={(a) => void decide(t.id, c, a)} onOpenLink={onOpenLink} />)}
        {t.ask ? <AskUserCard ask={t.ask} answerable={!busy && t === turns.at(-1)} onAnswer={(text) => void send(`${ar ? "الإجابات" : "Answers"}:\n${text}`)} /> : null}
        {t.role === "assistant" && (undoable(t) || t.undo) ? <UndoTurn turn={t} ar={ar} onUndo={() => void undoTurn(t)} /> : null}
      </>
    ) : undefined,
  }));

  const header = (
    <div className="flex items-center gap-2">
      <span className="grid size-7 place-items-center rounded-lg bg-aegold-50 text-aegold-700"><Wand2 className="size-4" aria-hidden /></span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold">{ar ? "مؤلف الصفحات" : "AI author"}</p>
        {pageName ? <p className="truncate text-xs text-muted-foreground">{pageName}</p> : null}
      </div>
      <ToggleGroup type="single" size="sm" value={mode} onValueChange={(v) => v && setMode(v as "build" | "plan")} aria-label={ar ? "الوضع" : "Mode"}>
        <ToggleGroupItem value="build">{ar ? "بناء" : "Build"}</ToggleGroupItem>
        <ToggleGroupItem value="plan">{ar ? "تخطيط" : "Plan"}</ToggleGroupItem>
      </ToggleGroup>
      {threads.length > 1 ? (
        <Button size="icon-sm" variant="ghost" aria-label={ar ? "المحادثات السابقة" : "Earlier conversations"} aria-expanded={showThreads} onClick={() => setShowThreads((s) => !s)}><History className="size-4" /></Button>
      ) : null}
      <Button size="icon-sm" variant="ghost" aria-label={ar ? "محادثة جديدة" : "New conversation"} disabled={busy} onClick={() => { setShowThreads(false); void load(null); }}>
        <MessageSquarePlus className="size-4" />
      </Button>
    </div>
  );

  return (
    <div className={cn("relative flex h-full min-h-0 flex-col", className)}>
      {showThreads ? (
        <div className="absolute inset-x-3 top-14 z-20 max-h-72 overflow-auto rounded-xl border border-sand-200 bg-white p-1 shadow-sand-lg">
          {threads.map((t) => (
            <button key={t.id} type="button" onClick={() => { setShowThreads(false); void load(t.id); }}
              className={cn("block w-full rounded-lg px-3 py-2 text-start text-sm hover:bg-sand-50", t.id === threadId && "bg-sand-100")}>
              <span className="block truncate font-medium">{t.preview ?? t.title}</span>
              <span className="text-xs text-muted-foreground">{new Date(t.lastMessageAt).toLocaleString(ar ? "ar-AE" : "en-GB", { dateStyle: "medium", timeStyle: "short" })} · {t.messages}</span>
            </button>
          ))}
        </div>
      ) : null}
      <ChatPanel
        className="h-full"
        bodyClassName="max-w-none"
        header={header}
        messages={messages}
        busy={busy}
        onSend={(t) => void send(t)}
        onStop={() => abort.current?.abort()}
        onAttach={(files) => setPending((p) => [...p, ...files].slice(0, 5))}
        accept=".csv,.xlsx,.xls,.json,.txt,.md,.pdf,.png,.jpg,.jpeg,.svg"
        composerTop={pending.length > 0 ? (
          <div className="flex flex-wrap gap-1.5 px-3 pt-2">
            {pending.map((f, i) => <PromptInputAttachment key={`${f.name}-${i}`} name={f.name} onRemove={() => setPending((p) => p.filter((_, j) => j !== i))} removeLabel={ar ? "إزالة" : "Remove"} />)}
          </div>
        ) : undefined}
        placeholder={mode === "plan" ? (ar ? "وصف المطلوب، وسأقترح خطة دون تغيير شيء" : "Describe what you want; I propose a plan and change nothing") : (ar ? "وصف لما يجب أن تفعله الصفحة أو ما يلزم تغييره" : "Describe what the page should do, or what to change")}
        labels={{ send: ar ? "إرسال" : "Send", escalate: "", thinking: ar ? "يعمل…" : "Working…", attach: ar ? "إرفاق ملف" : "Attach a file", stop: ar ? "إيقاف" : "Stop" }}
        assistantName={ar ? "مؤلف الصفحات" : "AI author"}
        assistantIcon={<Wand2 className="size-3.5" />}
        suggestions={suggestions ?? (ar
          ? ["فحص الصفحة وبيان ما يحتاج إلى إصلاح", "إضافة رسم بياني للاتجاه مع المصدر", "تحسين عرض الصفحة على الشاشات الصغيرة"]
          : ["Check the page and tell me what needs fixing", "Add a trend chart with its source", "Make the page work well on small screens"])}
        emptyState={loaded ? (
          <div className="rounded-xl border border-dashed border-sand-300 p-4 text-sm text-muted-foreground">
            {ar ? "يكفي وصف ما يجب أن تفعله الصفحة أو ما يلزم تغييره. أعمل على هذه المسودة، وأفحص كل تغيير بالعربية والإنجليزية، ولا يتغير شيء منشور." : "Tell me what the page should do, or what to change. I work on this draft, check every change in English and Arabic, and nothing published changes."}
          </div>
        ) : <div className="flex justify-center py-8"><Loader2 className="size-5 animate-spin text-aegold-700" /></div>}
      />
    </div>
  );
}
